import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, RefreshCw, Loader2, Copy, Check, DatabaseBackup, ChevronLeft, ChevronRight, X } from 'lucide-react';
// import type { Volume, Ambiente, Regiao } from '@prisma/client'; // Removido temporariamente
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api'; // Importar api

// Interface temporária com any
type VolumeWithDetails = any; 
type Ambiente = any; // Adicionar tipo Ambiente

// Interface para resposta paginada
interface PaginatedResponse {
  data: VolumeWithDetails[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Adicionar função auxiliar para formatar dias da semana
const formatWeekdays = (weekdays: string[] | undefined | null): string => {
  if (!weekdays || weekdays.length === 0) return 'Nenhum dia';
  const labels: Record<string, string> = {
    mon: 'Seg',
    tue: 'Ter',
    wed: 'Qua',
    thu: 'Qui',
    fri: 'Sex',
    sat: 'Sáb',
    sun: 'Dom',
  };
  return weekdays.map(day => labels[day.toLowerCase()] || day).join(', ');
};

export const VolumeList = () => {
  const [volumes, setVolumes] = useState<VolumeWithDetails[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [copiedVolumeId, setCopiedVolumeId] = useState<string | null>(null); // Estado para ID copiado
  const [processingVolume, setProcessingVolume] = useState<{ id: string; action: 'backup' } | null>(null); // Estado para loading do backup de volume
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { addToast } = useToast();

  // --- Estados e Lógica de Filtros (Início) ---
  const [ambientes, setAmbientes] = useState<Ambiente[]>([]);
  const [selectedAmbiente, setSelectedAmbiente] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  useEffect(() => {
    const fetchAmbientes = async () => {
      try {
        const response = await api.get<{ data: Ambiente[] }>('/api/ambientes');
        if (response.data && Array.isArray(response.data.data)) {
          setAmbientes(response.data.data);
        } else {
          setAmbientes([]);
        }
      } catch (error) {
        console.error('Erro ao buscar ambientes:', error);
        setAmbientes([]);
      }
    };
    fetchAmbientes();
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);
  // --- Estados e Lógica de Filtros (Fim) ---

  // Modificar fetchVolumes para usar filtros
  useEffect(() => {
    fetchVolumes(1); // Busca na montagem ou quando filtros mudam
  }, [selectedAmbiente, debouncedSearchTerm]); // Dependências

  const fetchVolumes = async (page = 1) => {
    setIsRefreshing(true);
    try {
      const params: any = {
        page,
        pageSize
      };
      if (selectedAmbiente) {
        params.ambienteId = selectedAmbiente;
      }
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      const response = await api.get<PaginatedResponse>('/api/volumes', { params });
      setVolumes(response.data.data);
      setTotalItems(response.data.total);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.page);
    } catch (error: any) {
      console.error('Error fetching volumes:', error);
      addToast({ type: 'error', title: 'Erro ao carregar volumes', description: error.response?.data?.error || error.message });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handler para mudar de página
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchVolumes(page);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este volume?')) return;

    try {
      // Usar api.delete com o caminho correto
      const response = await api.delete(`/api/volumes/${id}`);

      if (response.status === 204) {
        fetchVolumes(currentPage); // Atualiza a lista na página atual após exclusão
        addToast({ type: 'success', title: 'Volume excluído com sucesso!' });
      } else {
        console.error('Erro ao excluir volume, status:', response.status, response.data);
        addToast({ type: 'error', title: 'Erro ao excluir volume', description: `Status: ${response.status}` });
      }
    } catch (error: any) {
      console.error('Error deleting volume:', error);
      addToast({ type: 'error', title: 'Erro ao excluir volume', description: error.response?.data?.error || error.message });
    }
  };

  const handleSyncMGC = async () => {
    setIsSyncing(true);
    addToast({ type: 'info', title: 'Iniciando sincronização de volumes...' });
    try {
      // Usar api.post com o caminho correto
      const response = await api.post('/api/volumes/sync');

      if (response.status === 200) {
        await fetchVolumes(1); // Voltar para a primeira página após sincronização
        addToast({ type: 'success', title: 'Sincronização de volumes concluída!' });
      } else {
        console.error('Falha na sincronização, status:', response.status, response.data);
        const errorDesc = response.data?.error || `Status: ${response.status}`;
        addToast({ type: 'error', title: 'Falha na sincronização', description: errorDesc });
        throw new Error('Falha na sincronização de volumes');
      }
    } catch (error: any) {
      console.error('Erro ao sincronizar volumes com MGC:', error);
      if (!error.response) {
        addToast({ type: 'error', title: 'Erro de rede', description: 'Não foi possível conectar ao servidor para sincronizar.' });
      }
      // O addToast dentro do else acima já cobre erros da API
    } finally {
      setIsSyncing(false);
    }
  };

  // Assumindo que Regiao ainda não pode ser importado, usar string
  const getRegiaoLabel = (regiao: string) => {
     const labels: Record<string, string> = {
      br_se1: 'BR-SE1',
      br_mgl1: 'BR-MGL1',
      br_se2: 'BR-SE2',
      br_ne1: 'BR-NE1' // Adicionado BR-NE1 que vi em outro arquivo
    };
    return labels[regiao] || regiao;
  };

  // TODO: Adicionar função para formatar status_magalu se necessário
  // Exemplo:
  const getStatusMagaluStyle = (status: string) => {
    // Adicione lógica para retornar classes CSS baseadas no status
    if (status === 'available') return 'text-green-600';
    if (status === 'in-use') return 'text-blue-600';
    return 'text-gray-600';
  };

  // Função para copiar ID
  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedVolumeId(id);
      addToast({
        type: 'success',
        title: 'ID Magalu copiado!'
      });
      setTimeout(() => setCopiedVolumeId(null), 2000); // Limpa após 2 segundos
    } catch (error) {
      console.error("Erro ao copiar ID do volume:", error);
      addToast({
        type: 'error',
        title: 'Erro ao copiar ID'
      });
    }
  };

  // Função para criar snapshot de volume
  const handleBackupVolume = async (id: string, nome: string) => {
    setProcessingVolume({ id, action: 'backup' });
    addToast({ type: 'info', title: `Iniciando backup para volume ${nome}...` });
    try {
      // Usar api.post com o caminho correto
      const response = await api.post(`/api/volumes/${id}/snapshot`);
      
      if (response.status === 201) {
        console.log("Resposta API Snapshot Volume:", response.data);
        addToast({ type: 'success', title: 'Comando de backup de volume enviado!', description: response.data?.message });
      } else {
        console.error('Erro ao solicitar backup, status:', response.status, response.data);
        const errorMessage = response.data?.error || response.data?.message || 'Falha ao solicitar backup de volume';
        addToast({ type: 'error', title: 'Erro no backup do volume', description: errorMessage });
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error(`Erro ao solicitar backup para volume ${nome}:`, error);
      if (!error.response) {
         addToast({ type: 'error', title: 'Erro no backup do volume', description: error.message });
      }
       // O addToast dentro do else já cobre erros da API
    } finally {
      setProcessingVolume(null);
    }
  };

  // Handlers para mudança nos filtros
  const handleAmbienteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAmbiente(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Volumes</h1>
        <div className="flex gap-2">
          <button
            onClick={handleSyncMGC}
            disabled={isSyncing || isRefreshing}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            Sincronizar MGC
          </button>
        </div>
      </div>

      <div className="flex gap-4 mb-6 items-end">
        <div className="w-64">
          <label htmlFor="volume-search" className="block text-sm font-medium text-gray-700 mb-1">
            Buscar por nome
          </label>
          <div className="relative">
            <input
              type="text"
              id="volume-search"
              placeholder="Digite o nome..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 pr-8"
              disabled={isRefreshing || isSyncing}
            />
            {searchTerm && (
              <button 
                type="button"
                onClick={() => setSearchTerm('')} 
                className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                title="Limpar busca"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="w-64">
          <label htmlFor="volume-ambiente-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Ambiente
          </label>
          <div className="relative">
            <select
              id="volume-ambiente-filter"
              value={selectedAmbiente}
              onChange={handleAmbienteChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 pr-12 appearance-none"
              disabled={isRefreshing || isSyncing}
            >
              <option value="">Todos Ambientes</option>
              {ambientes.map((ambiente) => (
                <option key={ambiente.id} value={ambiente.id}>
                  {ambiente.nome}
                </option>
              ))}
            </select>
            {selectedAmbiente && (
              <button 
                type="button"
                onClick={() => setSelectedAmbiente('')} 
                className="absolute inset-y-0 right-7 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                title="Limpar seleção"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ambiente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamanho</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Região</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Instância Anexada</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Snapshot</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ativo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status Magalu</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isRefreshing && (
              <tr>
                <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                  Carregando...
                </td>
              </tr>
            )}
            {!isRefreshing && volumes.length === 0 && (
              <tr>
                <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                  Nenhum volume encontrado.
                </td>
              </tr>
            )}
            {!isRefreshing && volumes.map((volume) => {
              // Calcular estado de loading para este volume
              const isProcessingThisVolume = processingVolume?.id === volume.id && processingVolume?.action === 'backup';
              // Volume pode ter snapshot a qualquer momento?
              const canBackupVolume = true; // Ou adicionar lógica baseada em volume.status_magalu se necessário
              
              return (
                <tr key={volume.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 mr-2">{volume.nome}</span>
                      <button
                        onClick={() => handleCopyId(volume.magalu_id)}
                        className="text-gray-400 hover:text-gray-600"
                        title={`Copiar ID: ${volume.magalu_id}`}
                      >
                        {copiedVolumeId === volume.magalu_id ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{volume.ambiente?.nome || '-'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{volume.tamanho} GB</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{getRegiaoLabel(volume.regiao)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900" title={`ID Magalu VM: ${volume.magalu_vm_id || 'N/A'}`}>
                      {volume.vmNome || volume.magalu_vm_id || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{volume.snapshot_time || '-'}</div>
                    <div className="text-xs text-gray-500 mt-1">{formatWeekdays(volume.weekdays)}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Retenção: {volume.snapshot_retention_limit != null ? volume.snapshot_retention_limit : 'Sem limite'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${volume.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {volume.ativo ? 'Sim' : 'Não'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getStatusMagaluStyle(volume.status_magalu)}`}>{volume.status_magalu}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end items-center gap-3">
                       {/* Botão Backup Volume */}
                       <button
                          onClick={() => handleBackupVolume(volume.id, volume.nome)}
                          disabled={isProcessingThisVolume || isSyncing || isRefreshing || !canBackupVolume}
                          className={`text-purple-600 hover:text-purple-800 disabled:opacity-50 disabled:cursor-not-allowed`}
                          title="Criar Backup (Snapshot) do Volume"
                       >
                         {isProcessingThisVolume ? <Loader2 className="w-4 h-4 animate-spin" /> : <DatabaseBackup className="w-4 h-4" />}
                       </button>

                       {/* Botão Editar Volume */}
                       <Link 
                          to={`/volumes/${volume.id}`} 
                          className={`text-blue-600 hover:text-blue-900 ${isProcessingThisVolume ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                          title="Editar Agendamento do Volume"
                       >
                          <Pencil className="w-4 h-4" />
                       </Link>

                       {/* Botão Excluir Volume */}
                       <button 
                          onClick={() => handleDelete(volume.id)} 
                          className={`text-red-600 hover:text-red-900 ${isProcessingThisVolume ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                          disabled={isProcessingThisVolume || isSyncing || isRefreshing}
                          title="Excluir Registro do Volume"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {/* Paginação */}
        {!isRefreshing && volumes.length > 0 && (
          <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{volumes.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}</span> a <span className="font-medium">{Math.min(currentPage * pageSize, totalItems)}</span> de <span className="font-medium">{totalItems}</span> volumes
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Paginação">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Anterior</span>
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  
                  {/* Páginas numéricas */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(
                      totalPages - 4, 
                      currentPage - 2
                    )) + i;
                    
                    if (pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum 
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' 
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          } text-sm font-medium`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Próximo</span>
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 